import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDayRender } from 'react-day-picker';
import { it, enGB } from 'date-fns/locale';
import { CalendarDays, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const PickerDay = ({ date, displayMonth }) => {
  const ref = useRef(null);
  const day = useDayRender(date, displayMonth, ref);
  if (day.isHidden) return <div role="gridcell" />;
  return <button {...day.buttonProps} ref={ref} type="button" data-testid={`live-datetime-day-${dayKey(date)}`}>{date.getDate()}</button>;
};

// Keep draft values local until Confirm. Conversion to UTC still happens
// exclusively in CreateLiveDialog's existing submit path.
export const LiveDateTimePicker = ({ value, onChange }) => {
  const { t, i18n } = useTranslation('lives');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [month, setMonth] = useState(() => new Date(value));
  const [datePart = '', timePart = '00:00'] = (draft || '').split('T');
  const [hours = '00', minutes = '00'] = timePart.split(':');
  const selected = datePart ? new Date(`${datePart}T12:00:00`) : undefined;
  const locale = i18n.language.startsWith('en') ? enGB : it;
  const valid = draft && Number.isFinite(new Date(draft).getTime());
  const changeOpen = (next) => {
    if (next) { setDraft(value); setMonth(new Date(value)); }
    setOpen(next);
  };
  const confirm = () => { if (valid) { onChange(draft); setOpen(false); } };
  return <Popover open={open} onOpenChange={changeOpen}>
    <PopoverTrigger asChild>
      <Button type="button" variant="outline" aria-label={t('datetime.open')} aria-expanded={open}
        className="h-10 w-full min-w-0 justify-between gap-2 bg-background px-3 font-normal"
        data-testid="live-datetime-trigger">
        <span className="min-w-0 text-xs" data-testid="live-datetime-value">{value ? new Date(value).toLocaleString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('datetime.choose')}</span>
        <CalendarDays className="h-5 w-5 shrink-0 gold-text" data-testid="live-datetime-icon" />
      </Button>
    </PopoverTrigger>
    <PopoverContent align="start" sideOffset={8} collisionPadding={12}
      className="z-[60] w-[min(300px,calc(100vw-24px))] max-h-[calc(100dvh-24px)] overflow-y-auto p-3"
      style={{ maxHeight: 'min(var(--radix-popover-content-available-height), calc(100dvh - 24px))' }}
      data-testid="live-datetime-popover">
      <div className="flex items-center justify-between gap-2">
        <Button type="button" size="icon" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          aria-label={t('datetime.previous_month')} data-testid="live-datetime-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <p className="text-sm font-semibold" data-testid="live-datetime-month">{month.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}</p>
        <Button type="button" size="icon" variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          aria-label={t('datetime.next_month')} data-testid="live-datetime-next"><ChevronRight className="h-4 w-4" /></Button>
      </div>
      <Calendar mode="single" required selected={selected} month={month} onMonthChange={setMonth} locale={locale} fixedWeeks
        onSelect={(date) => date && setDraft(`${dayKey(date)}T${hours}:${minutes}`)} components={{ Day: PickerDay }}
        className="flex justify-center p-0" classNames={{ caption: 'hidden', month: 'space-y-1', row: 'flex w-full mt-1' }} data-testid="live-datetime-calendar" />
      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3">
        <label className="text-xs text-muted-foreground" data-testid="live-datetime-hours-label">{t('datetime.hours')}
          <select value={hours} onChange={(e) => setDraft(`${datePart}T${e.target.value}:${minutes}`)}
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-base text-foreground" data-testid="live-datetime-hours">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={pad(h)}>{pad(h)}</option>)}
          </select>
        </label>
        <label className="text-xs text-muted-foreground" data-testid="live-datetime-minutes-label">{t('datetime.minutes')}
          <select value={minutes} onChange={(e) => setDraft(`${datePart}T${hours}:${e.target.value}`)}
            className="mt-1 h-10 w-full rounded-md border border-input bg-background px-2 text-base text-foreground" data-testid="live-datetime-minutes">
            {Array.from({ length: 60 }, (_, m) => <option key={m} value={pad(m)}>{pad(m)}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <Button type="button" onClick={confirm} disabled={!valid} className="gold-gradient w-full text-xs font-semibold text-black" data-testid="live-datetime-confirm">
          <Check className="mr-1.5 h-4 w-4" />{t('datetime.confirm')}
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="live-datetime-cancel">{t('datetime.cancel')}</Button>
      </div>
    </PopoverContent>
  </Popover>;
};